import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Segmented, Modal, Area, Field, Bar } from "@/components/kit";
import { ROMAN, fmtDate, isOverdue, statusColor } from "@/lib/util";
import { DateField } from "@/components/DateField";

const FILTERS = ["All", "Open", "Kept", "Slipped"];

export const CommitmentRow = ({ c, refresh, canEdit }) => {
  const { memberById, meetings } = useTable();
  const m = memberById(c.memberId);
  const [slipOpen, setSlipOpen] = useState(false);
  const [note, setNote] = useState("");
  const overdue = isOverdue(c);

  const set = async (status, closedNote) => {
    try {
      await api.put(`/commitments/${c.id}`, { status, ...(closedNote !== undefined ? { closedNote } : {}) });
      await refresh();
      toast.success(status === "Kept" ? "Kept." : status === "Slipped" ? "Recorded." : "Reopened.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try { await api.delete(`/commitments/${c.id}`); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="p-4" data-testid={`commitment-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm">{m?.name || "Member"}</div>
          <p className="serif text-lg leading-snug mt-0.5">{c.text}</p>
          <div className="flex gap-2 mt-2.5 flex-wrap">
            <Chip>Due {fmtDate(c.dueDate)}</Chip>
            <Chip color={statusColor(c.status)}>{c.status}</Chip>
            {overdue && <Chip color="var(--rust)">Overdue</Chip>}
          </div>
          {c.closedNote && (
            <p className="text-[13px] mt-3" style={{ color: "var(--ink-mute)" }}>“{c.closedNote}”</p>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="flex gap-2 mt-4 flex-wrap">
          {c.status === "Open" ? (
            <>
              <button data-testid={`keep-${c.id}`} className="btn-quiet" onClick={() => set("Kept")}>Kept ✓</button>
              <button data-testid={`slip-${c.id}`} className="btn-quiet" onClick={() => setSlipOpen(true)}>Slipped</button>
            </>
          ) : (
            <button data-testid={`reopen-${c.id}`} className="btn-quiet" onClick={() => set("Open")}>Reopen</button>
          )}
          <button data-testid={`delete-${c.id}`} className="btn-quiet" onClick={del}>Delete</button>
        </div>
      )}
      <Modal open={slipOpen} onClose={() => setSlipOpen(false)} title="The slip is the story" testId="slip-modal">
        <p className="text-[14px]">Bring the honest one.</p>
        <Area label="What happened" value={note} onChange={(e) => setNote(e.target.value)} data-testid="slip-note" />
        <button
          className="btn-ink w-full"
          data-testid="slip-confirm"
          onClick={async () => { await set("Slipped", note); setSlipOpen(false); setNote(""); }}
        >
          Record the slip
        </button>
      </Modal>
    </Card>
  );
};

export default function Ledger() {
  const { me, commitments, meetings, refresh, canWrite, activeMembers } = useTable();
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ text: "", dueDate: "", meetingId: "", memberId: "" });

  const counts = {
    All: commitments.length,
    Open: commitments.filter((c) => c.status === "Open").length,
    Kept: commitments.filter((c) => c.status === "Kept").length,
    Slipped: commitments.filter((c) => c.status === "Slipped").length,
  };

  const shown = commitments.filter((c) => filter === "All" || c.status === filter);
  const byMeeting = [...meetings].sort((a, b) => b.num - a.num);

  const create = async () => {
    try {
      await api.post("/commitments", {
        ...form,
        meetingId: form.meetingId || meetings[0]?.id,
        memberId: form.memberId || me.memberId,
      });
      setOpen(false);
      setForm({ text: "", dueDate: "", meetingId: "", memberId: "" });
      await refresh();
      toast.success("Commitment entered.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="ledger-page">
      <PageHead
        kicker="Accountability"
        title="The Ledger"
        sub="One countable commitment, one expiry date, per member, per chapter. Ownership — not theatre."
        right={canWrite && (
          <button data-testid="add-commitment" className="btn-ink" onClick={() => setOpen(true)}>Add</button>
        )}
      />

      <div className="flex gap-2 flex-wrap mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`ledger-filter-${f.toLowerCase()}`}
            onClick={() => setFilter(f)}
            className="chip"
            style={filter === f ? { background: "var(--ink-panel)", color: "#FFFFFF", borderColor: "var(--ink-panel)" } : {}}
          >
            {f} · {counts[f]}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Empty line="Nothing here yet. The close writes this page." testId="ledger-empty" />
      ) : (
        <div className="space-y-8">
          {byMeeting.map((m) => {
            const items = shown.filter((c) => c.meetingId === m.id);
            if (!items.length) return null;
            return (
              <div key={m.id}>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="roman text-3xl">{ROMAN[m.num - 1]}</span>
                  <span className="serif text-xl">{m.title || "Untitled"}</span>
                </div>
                <div className="space-y-3">
                  {items.map((c) => (
                    <CommitmentRow
                      key={c.id}
                      c={c}
                      refresh={refresh}
                      canEdit={canWrite && (me.isAdmin || c.memberId === me.memberId)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New commitment" testId="commitment-modal">
        <Area
          label="The commitment"
          value={form.text}
          data-testid="commitment-text"
          onChange={(e) => setForm({ ...form, text: e.target.value })}
        />
        <DateField
          label="Due date"
          value={form.dueDate}
          testId="commitment-due"
          onChange={(v) => setForm({ ...form, dueDate: v })}
        />
        <label className="block">
          <span className="kicker block mb-1.5">Chapter</span>
          <select
            className="field"
            data-testid="commitment-chapter"
            value={form.meetingId}
            onChange={(e) => setForm({ ...form, meetingId: e.target.value })}
          >
            {meetings.map((m) => (
              <option key={m.id} value={m.id}>Chapter {ROMAN[m.num - 1]} {m.title ? `— ${m.title}` : ""}</option>
            ))}
          </select>
        </label>
        {me.isAdmin && (
          <label className="block">
            <span className="kicker block mb-1.5">Member</span>
            <select
              className="field"
              data-testid="commitment-member"
              value={form.memberId}
              onChange={(e) => setForm({ ...form, memberId: e.target.value })}
            >
              <option value="">Me</option>
              {activeMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
        )}
        <button className="btn-ink w-full" data-testid="commitment-save" onClick={create}>Enter it</button>
      </Modal>
    </div>
  );
}
